import { NavLink } from 'react-router-dom'
import {Building2, ChevronLeft, ChevronRight, FileText, Landmark, LayoutGrid, ListChecks, MapPin, Settings, UserRound, Users, X} from 'lucide-react'
import { useAppAuth } from '@/contexts/AuthContext'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  /** Gaveta aberta no telemóvel */
  mobileOpen?: boolean
  onCloseMobile?: () => void
}

const roleLabels: Record<string, string> = {
  ADMIN: 'Administrador',
  MODERADOR: 'Moderador',
  GESTOR: 'Gestor',
  COORDENADOR: 'Coordenador',
}

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutGrid, roles: ['ADMIN', 'MODERADOR', 'GESTOR', 'COORDENADOR'] },
  { to: '/protocolos', label: 'Requerimentos', icon: ListChecks, roles: ['ADMIN', 'MODERADOR', 'GESTOR', 'COORDENADOR'] },
  { to: '/sublocais', label: 'Sub Locais', icon: MapPin, roles: ['ADMIN'] },
  { to: '/cidades', label: 'Cidades', icon: Building2, roles: ['ADMIN'] },
  { to: '/secretarias', label: 'Secretarias', icon: Landmark, roles: ['ADMIN'] },
  { to: '/usuarios', label: 'Utilizadores', icon: Users, roles: ['ADMIN'] },
  { to: '/configuracoes', label: 'Configurações', icon: Settings, roles: ['ADMIN'] },
  { to: '/perfil', label: 'Meu Perfil', icon: UserRound, roles: ['ADMIN', 'MODERADOR', 'GESTOR', 'COORDENADOR'] },
]

function Sidebar({ collapsed, onToggle, mobileOpen = false, onCloseMobile }: SidebarProps) {
  const { user } = useAppAuth()
  const role = user?.role || 'COORDENADOR'
  const itens = navItems.filter(item => item.roles.includes(role))

  return (
    <>
      {/* Fundo escurecido no telemóvel */}
      {mobileOpen && (
        <div
          onClick={onCloseMobile}
          className="lg:hidden fixed inset-0 z-40 bg-black/45 backdrop-blur-[1px] animate-[fadeIn_.18s_ease-out]"
        />
      )}

      <aside
        className={[
          'flex flex-col bg-white border-[#E7E9EC] transition-transform duration-200',
          // Telemóvel: gaveta deslizante
          'fixed inset-y-0 left-0 z-50 w-[272px] border-r',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop: barra lateral fixa
          'lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen lg:z-auto lg:transition-[width] lg:duration-200',
          collapsed ? 'lg:w-[68px]' : 'lg:w-[240px]',
        ].join(' ')}
      >
        <div className={`flex items-center h-16 px-4 border-b border-[#E7E9EC] ${collapsed ? 'lg:justify-center' : 'justify-between'}`}>
          <div className="flex items-center gap-2 overflow-hidden">
            <div className="w-8 h-8 rounded-lg bg-[#1E4FBF] flex items-center justify-center shrink-0">
              <FileText className="w-4.5 h-4.5 text-white" strokeWidth={1.75} />
            </div>
            <span className={`text-[15px] font-semibold text-[#12141A] tracking-tight truncate ${collapsed ? 'lg:hidden' : ''}`}>
              PROTOCOLEAI
            </span>
          </div>
          <button
            onClick={onCloseMobile}
            className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center text-[#6B7280] hover:bg-[#F5F6F8]"
            aria-label="Fechar menu"
          >
            <X className="w-5 h-5" strokeWidth={1.9} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 flex flex-col gap-1">
          {itens.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onCloseMobile}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13.5px] font-medium transition-colors ${
                  isActive
                    ? 'bg-[#EEF3FE] text-[#1E4FBF]'
                    : 'text-[#4B5563] hover:bg-[#F5F6F8] hover:text-[#12141A]'
                } ${collapsed ? 'lg:justify-center' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <item.icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
              <span className={`truncate ${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-[#E7E9EC]">
          <div className="lg:hidden flex items-center gap-2.5 px-2 pb-3">
            <div className="w-8 h-8 rounded-full bg-[#EEF3FE] flex items-center justify-center overflow-hidden shrink-0">
              {user?.fotoUrl
                ? <img src={user.fotoUrl} alt={user.nomeCompleto} className="w-full h-full object-cover" />
                : <Users className="w-4 h-4 text-[#1E4FBF]" strokeWidth={1.75} />}
            </div>
            <div className="min-w-0">
              <p className="text-[13px] font-medium text-[#12141A] truncate">{user?.nomeCompleto}</p>
              <p className="text-[11px] text-[#9CA3AF] truncate">{roleLabels[role] || role}</p>
            </div>
          </div>
          <button
            onClick={onToggle}
            className="hidden lg:flex w-full items-center justify-center py-2 rounded-lg text-[#6B7280] hover:bg-[#F5F6F8] hover:text-[#12141A] transition-colors"
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" strokeWidth={1.75} /> : <ChevronLeft className="w-4 h-4" strokeWidth={1.75} />}
          </button>
        </div>
      </aside>
    </>
  )
}

export default Sidebar
